import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faYoutube, faSpotify } from '@fortawesome/free-brands-svg-icons';
import { Icon } from '@/components/ui/Icon';
import { Tooltip } from '@/components/ui/Tooltip';
import { NumberStepper } from '@/components/ui/NumberStepper';
import { ICONS } from '@/constants/icons';

/**
 * Calm Mode - Premium ambient sounds for focus
 * Features:
 * - Multi-sound mixing (layer multiple sounds)
 * - Generated ambient sounds (white/pink/brown noise)
 * - Real audio files for nature sounds
 * - YouTube integration for custom music
 * - Spotify integration for custom music (embed only, no OAuth/Web API)
 * - Master volume control
 */

type TabType = 'sounds' | 'youtube' | 'spotify';

interface Sound {
  id: string;
  name: string;
  emoji: string;
  type: 'generated' | 'audio';
  url?: string;
  mood?: 'water' | 'nature' | 'fire' | 'urban' | 'noise';
}

// Cache-bust version to force browser to fetch fresh files
const AUDIO_VERSION = 'v2';
const sounds: Sound[] = [
  { id: 'white-noise', name: 'White Noise', emoji: '🌫️', type: 'generated', mood: 'noise' },
  { id: 'pink-noise', name: 'Pink Noise', emoji: '💗', type: 'generated', mood: 'noise' },
  { id: 'brown-noise', name: 'Brown Noise', emoji: '🤎', type: 'generated', mood: 'noise' },
  { id: 'rain', name: 'Rain', emoji: '🌧️', type: 'audio', url: `/audio/rain.mp3?${AUDIO_VERSION}`, mood: 'water' },
  { id: 'ocean', name: 'Ocean', emoji: '🌊', type: 'audio', url: `/audio/ocean.mp3?${AUDIO_VERSION}`, mood: 'water' },
  { id: 'forest', name: 'Forest', emoji: '🌲', type: 'audio', url: `/audio/forest.mp3?${AUDIO_VERSION}`, mood: 'nature' },
  { id: 'fireplace', name: 'Fireplace', emoji: '🔥', type: 'audio', url: `/audio/fireplace.mp3?${AUDIO_VERSION}`, mood: 'fire' },
  { id: 'cafe', name: 'Cafe', emoji: '☕', type: 'audio', url: `/audio/cafe.mp3?${AUDIO_VERSION}`, mood: 'urban' },
  { id: 'birds', name: 'Birds', emoji: '🐦', type: 'audio', url: `/audio/birds.mp3?${AUDIO_VERSION}`, mood: 'nature' },
];

// Quick presets - one-click sound mixes
interface Preset {
  id: string;
  name: string;
  emoji: string;
  description: string;
  sounds: string[];
}

const presets: Preset[] = [
  { id: 'coffee-shop', name: 'Coffee Shop', emoji: '☕', description: 'Cafe + Rain', sounds: ['cafe', 'rain'] },
  { id: 'deep-forest', name: 'Deep Forest', emoji: '🌲', description: 'Forest + Birds', sounds: ['forest', 'birds'] },
  { id: 'cozy-night', name: 'Cozy Night', emoji: '🔥', description: 'Fireplace + Rain', sounds: ['fireplace', 'rain'] },
  { id: 'ocean-breeze', name: 'Ocean Breeze', emoji: '🏖️', description: 'Ocean + Birds', sounds: ['ocean', 'birds'] },
  { id: 'deep-focus', name: 'Deep Focus', emoji: '🧠', description: 'Brown Noise + Rain', sounds: ['brown-noise', 'rain'] },
  { id: 'meditation', name: 'Meditation', emoji: '🧘', description: 'Pink Noise + Forest', sounds: ['pink-noise', 'forest'] },
];

// Timer options in minutes
const timerOptions = [
  { label: '15m', minutes: 15 },
  { label: '25m', minutes: 25 },
  { label: '45m', minutes: 45 },
  { label: '60m', minutes: 60 },
  { label: '90m', minutes: 90 },
];

// Mood-based gradients
const moodGradients: Record<string, string> = {
  water: 'from-blue-500 to-cyan-600',
  nature: 'from-green-500 to-emerald-600',
  fire: 'from-orange-500 to-red-600',
  urban: 'from-amber-600 to-yellow-700',
  noise: 'from-gray-500 to-slate-600',
  default: 'from-secondary-600 to-secondary-700',
  mixed: 'from-amber-700 to-primary-800',
};

// Audio instance tracking for multi-sound mixing
interface ActiveSound {
  id: string;
  audioContext?: AudioContext;
  sourceNode?: AudioBufferSourceNode;
  gainNode?: GainNode;
  audioElement?: HTMLAudioElement;
}

// Extract YouTube video ID from various URL formats
const extractYouTubeId = (url: string): string | null => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
    /youtube\.com\/playlist\?list=([^&\s]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// Check if URL is a playlist
const isPlaylist = (url: string): boolean => {
  return url.includes('playlist?list=') || url.includes('&list=');
};

// Spotify embed types (Option A: iframe embed only, no OAuth, no Web API)
type SpotifyEmbedType = 'playlist' | 'album' | 'track' | 'artist' | 'show' | 'episode';

interface SpotifyEmbed {
  type: SpotifyEmbedType;
  id: string;
}

const SPOTIFY_EMBED_TYPES: readonly SpotifyEmbedType[] = ['playlist', 'album', 'track', 'artist', 'show', 'episode'];

const isSpotifyEmbedType = (value: string): value is SpotifyEmbedType =>
  (SPOTIFY_EMBED_TYPES as readonly string[]).includes(value);

// Extract a Spotify embed type + id from a share link or spotify: URI.
// Handles open.spotify.com links (with or without locale prefix like /intl-de/),
// trailing query strings (?si=...), and spotify:type:id URIs. Returns null on
// anything unparseable - no network calls, pure client-side parsing.
const extractSpotifyEmbed = (url: string): SpotifyEmbed | null => {
  const trimmed = url.trim();
  if (!trimmed) return null;

  // spotify:playlist:37i9dQ... URI form
  const uriMatch = trimmed.match(/^spotify:(playlist|album|track|artist|show|episode):([a-zA-Z0-9]+)/i);
  if (uriMatch) {
    const type = uriMatch[1].toLowerCase();
    if (isSpotifyEmbedType(type)) {
      return { type, id: uriMatch[2] };
    }
  }

  // https://open.spotify.com/[intl-xx/]type/id[?si=...] web URL form
  const urlMatch = trimmed.match(
    /open\.spotify\.com\/(?:intl-[a-z]{2}\/)?(playlist|album|track|artist|show|episode)\/([a-zA-Z0-9]+)/i
  );
  if (urlMatch) {
    const type = urlMatch[1].toLowerCase();
    if (isSpotifyEmbedType(type)) {
      return { type, id: urlMatch[2] };
    }
  }

  return null;
};

// Spotify's compact 152px player (used for single track/episode embeds) is a
// wide horizontal card whose min-size exceeds this panel's narrow width,
// causing the iframe content to overflow and scroll. The 352px vertical
// embed fits the narrow panel for every type, so use it uniformly.
const getSpotifyEmbedHeight = (_type: SpotifyEmbedType): number => {
  return 352;
};

export const CalmMode = () => {
  const { t } = useTranslation(['dashboard', 'common']);
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('sounds');
  const [activeSounds, setActiveSounds] = useState<Set<string>>(() => {
    const saved = localStorage.getItem('calmMode_activeSounds');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem('calmMode_volume');
    return saved ? parseInt(saved, 10) : 50;
  });
  const [youtubeUrl, setYoutubeUrl] = useState(() => {
    return localStorage.getItem('calmMode_youtubeUrl') || '';
  });
  const [youtubeVideoId, setYoutubeVideoId] = useState<string | null>(null);
  const [isYoutubePlaying, setIsYoutubePlaying] = useState(false);
  const [youtubeError, setYoutubeError] = useState<string | null>(null);
  const [spotifyUrl, setSpotifyUrl] = useState(() => {
    return localStorage.getItem('calmMode_spotifyUrl') || '';
  });
  const [spotifyEmbed, setSpotifyEmbed] = useState<SpotifyEmbed | null>(null);
  const [spotifyError, setSpotifyError] = useState<string | null>(null);
  const [soundVolumes, setSoundVolumes] = useState<Record<string, number>>(() => {
    const saved = localStorage.getItem('calmMode_soundVolumes');
    return saved ? JSON.parse(saved) : {};
  });
  const [isInitialized, setIsInitialized] = useState(false);

  // Timer state - persisted to localStorage
  const [timerMinutes, setTimerMinutes] = useState<number | null>(() => {
    const saved = localStorage.getItem('calmMode_timerMinutes');
    return saved ? parseInt(saved, 10) : null;
  });
  const [timerEndTime, setTimerEndTime] = useState<number | null>(() => {
    const saved = localStorage.getItem('calmMode_timerEndTime');
    if (saved) {
      const endTime = parseInt(saved, 10);
      // Only restore if timer hasn't expired
      if (endTime > Date.now()) return endTime;
    }
    return null;
  });
  const [timeRemaining, setTimeRemaining] = useState<string>('');

  const dropdownRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const activeSoundsRef = useRef<Map<string, ActiveSound>>(new Map());
  const youtubePlayerRef = useRef<YT.Player | null>(null);
  const youtubeContainerRef = useRef<HTMLDivElement>(null);

  // Get volume for a sound (default 100)
  const getSoundVolume = (soundId: string) => soundVolumes[soundId] ?? 100;

  // Update individual sound volume
  const updateSoundVolume = useCallback((soundId: string, newVolume: number) => {
    setSoundVolumes(prev => ({ ...prev, [soundId]: newVolume }));

    const activeSound = activeSoundsRef.current.get(soundId);
    if (activeSound) {
      const effectiveVolume = (newVolume / 100) * (volume / 100);
      if (activeSound.gainNode) {
        activeSound.gainNode.gain.value = effectiveVolume;
      }
      if (activeSound.audioElement) {
        activeSound.audioElement.volume = effectiveVolume;
      }
    }
  }, [volume]);

  // Save volume to localStorage
  useEffect(() => {
    localStorage.setItem('calmMode_volume', volume.toString());
  }, [volume]);

  // Save activeSounds to localStorage
  useEffect(() => {
    localStorage.setItem('calmMode_activeSounds', JSON.stringify(Array.from(activeSounds)));
  }, [activeSounds]);

  // Save soundVolumes to localStorage
  useEffect(() => {
    localStorage.setItem('calmMode_soundVolumes', JSON.stringify(soundVolumes));
  }, [soundVolumes]);

  // Save youtubeUrl to localStorage
  useEffect(() => {
    localStorage.setItem('calmMode_youtubeUrl', youtubeUrl);
  }, [youtubeUrl]);

  // Save spotifyUrl to localStorage
  useEffect(() => {
    localStorage.setItem('calmMode_spotifyUrl', spotifyUrl);
  }, [spotifyUrl]);

  // Save timer state to localStorage
  useEffect(() => {
    if (timerEndTime) {
      localStorage.setItem('calmMode_timerEndTime', timerEndTime.toString());
      localStorage.setItem('calmMode_timerMinutes', (timerMinutes || 0).toString());
    } else {
      localStorage.removeItem('calmMode_timerEndTime');
      localStorage.removeItem('calmMode_timerMinutes');
    }
  }, [timerEndTime, timerMinutes]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Load YouTube IFrame API
  useEffect(() => {
    if (typeof window !== 'undefined' && !window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
    }
  }, []);

  // Generate noise buffer
  const generateNoiseBuffer = useCallback((type: 'white' | 'pink' | 'brown', audioContext: AudioContext) => {
    const bufferSize = 2 * audioContext.sampleRate;
    const buffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
    const output = buffer.getChannelData(0);

    if (type === 'white') {
      for (let i = 0; i < bufferSize; i++) {
        output[i] = Math.random() * 2 - 1;
      }
    } else if (type === 'pink') {
      let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
      for (let i = 0; i < bufferSize; i++) {
        const white = Math.random() * 2 - 1;
        b0 = 0.99886 * b0 + white * 0.0555179;
        b1 = 0.99332 * b1 + white * 0.0750759;
        b2 = 0.96900 * b2 + white * 0.1538520;
        b3 = 0.86650 * b3 + white * 0.3104856;
        b4 = 0.55000 * b4 + white * 0.5329522;
        b5 = -0.7616 * b5 - white * 0.0168980;
        output[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
        b6 = white * 0.115926;
      }
    } else if (type === 'brown') {
      let lastOut = 0;
      for (let i = 0; i < bufferSize; i++) {
        const white = Math.random() * 2 - 1;
        output[i] = (lastOut + 0.02 * white) / 1.02;
        lastOut = output[i];
        output[i] *= 3.5;
      }
    }

    return buffer;
  }, []);

  // Start a generated noise sound
  const startGeneratedNoise = useCallback((soundId: string, noiseType: 'white' | 'pink' | 'brown') => {
    const audioContext = new AudioContext();
    const gainNode = audioContext.createGain();
    gainNode.gain.value = volume / 100;
    gainNode.connect(audioContext.destination);

    const buffer = generateNoiseBuffer(noiseType, audioContext);
    const source = audioContext.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.connect(gainNode);
    source.start();

    const activeSound: ActiveSound = {
      id: soundId,
      audioContext,
      sourceNode: source,
      gainNode,
    };

    activeSoundsRef.current.set(soundId, activeSound);
  }, [volume, generateNoiseBuffer]);

  // Start an audio file sound
  const startAudioFile = useCallback((soundId: string, url: string) => {
    const audio = new Audio(url);
    audio.loop = true;
    audio.volume = volume / 100;
    audio.play().catch((err) => {
      console.error('Audio play failed:', err);
    });

    const activeSound: ActiveSound = {
      id: soundId,
      audioElement: audio,
    };

    activeSoundsRef.current.set(soundId, activeSound);
  }, [volume]);

  // Stop a specific sound
  const stopSound = useCallback((soundId: string) => {
    const activeSound = activeSoundsRef.current.get(soundId);
    if (!activeSound) return;

    if (activeSound.sourceNode) {
      activeSound.sourceNode.stop();
    }
    if (activeSound.audioContext) {
      activeSound.audioContext.close();
    }
    if (activeSound.audioElement) {
      activeSound.audioElement.pause();
      activeSound.audioElement.src = '';
    }

    activeSoundsRef.current.delete(soundId);
  }, []);

  // Stop all sounds (keeps timer running)
  const stopAllSounds = useCallback(() => {
    activeSoundsRef.current.forEach((_, soundId) => {
      stopSound(soundId);
    });
    setActiveSounds(new Set());

    // Stop YouTube too
    if (youtubePlayerRef.current) {
      youtubePlayerRef.current.stopVideo();
    }
    setIsYoutubePlaying(false);

    // Stop Spotify too - a plain iframe can't be paused via JS, so "stop"
    // means unmounting it, which removes it from the DOM and halts playback.
    setSpotifyEmbed(null);
  }, [stopSound]);

  // Play completion chime
  const playCompletionChime = useCallback(() => {
    // Create a gentle bell sound using Web Audio API
    const audioContext = new AudioContext();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Bell-like tone
    oscillator.frequency.setValueAtTime(830, audioContext.currentTime); // A5 note
    oscillator.type = 'sine';

    // Gentle fade in and out
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.1);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 1.5);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 1.5);

    // Second chime (slightly lower)
    setTimeout(() => {
      const osc2 = audioContext.createOscillator();
      const gain2 = audioContext.createGain();
      osc2.connect(gain2);
      gain2.connect(audioContext.destination);
      osc2.frequency.setValueAtTime(622, audioContext.currentTime); // E5 note
      osc2.type = 'sine';
      gain2.gain.setValueAtTime(0, audioContext.currentTime);
      gain2.gain.linearRampToValueAtTime(0.25, audioContext.currentTime + 0.1);
      gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 2);
      osc2.start(audioContext.currentTime);
      osc2.stop(audioContext.currentTime + 2);
    }, 400);
  }, []);

  // Send browser notification
  const sendNotification = useCallback((title: string, body: string) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, {
        body,
        icon: '/images/logo.png',
        badge: '/images/logo.png',
        tag: 'calm-mode-timer',
      });
    }
  }, []);

  // Request notification permission
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  }, []);

  // State for completion message
  const [showCompletion, setShowCompletion] = useState(false);
  const [lastCompletedMinutes, setLastCompletedMinutes] = useState<number | null>(null);

  // State for custom timer input
  const [showCustomTimer, setShowCustomTimer] = useState(false);
  const [customMinutes, setCustomMinutes] = useState(25);

  // State for collapsed sections (more compact UI)
  const [presetsExpanded, setPresetsExpanded] = useState(false);
  const [soundsExpanded, setSoundsExpanded] = useState(false);

  // Stop everything including timer (called when timer expires)
  const stopEverything = useCallback(() => {
    const completedMinutes = timerMinutes || 0;

    stopAllSounds();

    // Clear timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setTimerMinutes(null);
    setTimerEndTime(null);
    setTimeRemaining('');

    // Only show completion if there was actually a timer
    if (completedMinutes > 0) {
      // Play completion chime
      playCompletionChime();

      // Send browser notification with proper grammar
      const minuteWord = completedMinutes === 1 ? 'minute' : 'minutes';
      sendNotification(
        '🎉 Focus session complete!',
        `Great job! You focused for ${completedMinutes} ${minuteWord}.`
      );

      // Show completion UI
      setLastCompletedMinutes(completedMinutes);
      setShowCompletion(true);

      // Auto-hide after 10 seconds
      setTimeout(() => setShowCompletion(false), 10000);
    }
  }, [stopAllSounds, timerMinutes, playCompletionChime, sendNotification]);

  // Start a preset (activate multiple sounds at once)
  const activatePreset = useCallback((preset: Preset) => {
    // Stop all current sounds first
    activeSoundsRef.current.forEach((_, soundId) => {
      stopSound(soundId);
    });

    // Start all preset sounds
    const newActiveSounds = new Set<string>();
    preset.sounds.forEach((soundId) => {
      const sound = sounds.find(s => s.id === soundId);
      if (!sound) return;

      if (sound.type === 'generated') {
        const noiseType = soundId.replace('-noise', '') as 'white' | 'pink' | 'brown';
        startGeneratedNoise(soundId, noiseType);
      } else if (sound.type === 'audio' && sound.url) {
        startAudioFile(soundId, sound.url);
      }
      newActiveSounds.add(soundId);
    });

    setActiveSounds(newActiveSounds);
  }, [stopSound, startGeneratedNoise, startAudioFile]);

  // Format time remaining - shows "25m" for >= 1 min, "45s" for < 1 min
  const formatTimeRemaining = useCallback((remaining: number): string => {
    const mins = Math.floor(remaining / 60000);
    const secs = Math.floor((remaining % 60000) / 1000);
    if (mins >= 1) {
      return `${mins}m`;
    }
    return `${secs}s`;
  }, []);

  // Start timer
  const startTimer = useCallback((minutes: number) => {
    // Request notification permission
    requestNotificationPermission();

    // Hide any completion message
    setShowCompletion(false);

    // Clear existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    const endTime = Date.now() + minutes * 60 * 1000;
    setTimerMinutes(minutes);
    setTimerEndTime(endTime);

    // Update countdown
    const updateTimer = () => {
      const remaining = endTime - Date.now();
      if (remaining <= 0) {
        // Timer finished - stop everything
        stopEverything();
      } else {
        setTimeRemaining(formatTimeRemaining(remaining));
      }
    };

    // Initial update
    updateTimer();

    // Update every second when < 1 min, every 10 seconds otherwise
    timerRef.current = setInterval(updateTimer, 1000);
  }, [stopEverything, formatTimeRemaining, requestNotificationPermission]);

  // Restore timer on mount if it was running
  useEffect(() => {
    if (timerEndTime && timerEndTime > Date.now() && !timerRef.current) {
      const remaining = timerEndTime - Date.now();
      setTimeRemaining(formatTimeRemaining(remaining));

      // Restart the interval
      timerRef.current = setInterval(() => {
        const rem = timerEndTime - Date.now();
        if (rem <= 0) {
          stopEverything();
        } else {
          setTimeRemaining(formatTimeRemaining(rem));
        }
      }, 1000);
    }
  }, [timerEndTime, formatTimeRemaining, stopEverything]);

  // Cancel timer
  const cancelTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setTimerMinutes(null);
    setTimerEndTime(null);
    setTimeRemaining('');
  }, []);

  // Get dynamic gradient based on active sounds' moods
  const getDynamicGradient = useCallback(() => {
    if (activeSounds.size === 0) return moodGradients.default;

    const moods = Array.from(activeSounds)
      .map(id => sounds.find(s => s.id === id)?.mood)
      .filter(Boolean) as string[];

    if (moods.length === 0) return moodGradients.default;

    // If all same mood, use that gradient
    const uniqueMoods = [...new Set(moods)];
    if (uniqueMoods.length === 1) {
      return moodGradients[uniqueMoods[0]] || moodGradients.default;
    }

    // Mixed moods - use mixed gradient
    return moodGradients.mixed;
  }, [activeSounds]);

  // Keyboard shortcut: Ctrl/Cmd + Shift + M to toggle
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd + Shift + M
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'm') {
        e.preventDefault();
        if (activeSounds.size > 0 || isYoutubePlaying || spotifyEmbed) {
          stopAllSounds();
        } else {
          setIsOpen(prev => !prev);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeSounds.size, isYoutubePlaying, spotifyEmbed, stopAllSounds]);

  // Toggle a sound on/off
  const toggleSound = useCallback((soundId: string) => {
    const sound = sounds.find(s => s.id === soundId);
    if (!sound) return;

    if (activeSounds.has(soundId)) {
      // Stop this sound
      stopSound(soundId);
      setActiveSounds(prev => {
        const next = new Set(prev);
        next.delete(soundId);
        return next;
      });
    } else {
      // Start this sound
      if (sound.type === 'generated') {
        const noiseType = soundId.replace('-noise', '') as 'white' | 'pink' | 'brown';
        startGeneratedNoise(soundId, noiseType);
      } else if (sound.type === 'audio' && sound.url) {
        startAudioFile(soundId, sound.url);
      }
      setActiveSounds(prev => {
        const next = new Set(prev);
        next.add(soundId);
        return next;
      });
    }
  }, [activeSounds, stopSound, startGeneratedNoise, startAudioFile]);

  // Restart sounds on mount (persist across page refresh)
  useEffect(() => {
    if (isInitialized) return;

    // Small delay to ensure audio context is ready
    const timer = setTimeout(() => {
      activeSounds.forEach((soundId) => {
        const sound = sounds.find(s => s.id === soundId);
        if (!sound) return;

        // Only start if not already playing
        if (!activeSoundsRef.current.has(soundId)) {
          if (sound.type === 'generated') {
            const noiseType = soundId.replace('-noise', '') as 'white' | 'pink' | 'brown';
            startGeneratedNoise(soundId, noiseType);
          } else if (sound.type === 'audio' && sound.url) {
            startAudioFile(soundId, sound.url);
          }
        }
      });
      setIsInitialized(true);
    }, 100);

    return () => clearTimeout(timer);
  }, [isInitialized, activeSounds, startGeneratedNoise, startAudioFile]);

  // Update volume for all active sounds (master affects all)
  useEffect(() => {
    activeSoundsRef.current.forEach((activeSound, soundId) => {
      const soundVol = soundVolumes[soundId] ?? 100;
      const effectiveVolume = (soundVol / 100) * (volume / 100);
      if (activeSound.gainNode) {
        activeSound.gainNode.gain.value = effectiveVolume;
      }
      if (activeSound.audioElement) {
        activeSound.audioElement.volume = effectiveVolume;
      }
    });
    if (youtubePlayerRef.current && isYoutubePlaying) {
      youtubePlayerRef.current.setVolume(volume);
    }
  }, [volume, isYoutubePlaying, soundVolumes]);

  // Play YouTube video
  const playYouTube = () => {
    const videoId = extractYouTubeId(youtubeUrl);
    if (!videoId) {
      setYoutubeError('Invalid YouTube URL');
      return;
    }

    setYoutubeError(null);
    setYoutubeVideoId(videoId);

    if (window.YT && window.YT.Player) {
      if (youtubePlayerRef.current) {
        youtubePlayerRef.current.destroy();
      }

      const isPlaylistUrl = isPlaylist(youtubeUrl);

      youtubePlayerRef.current = new window.YT.Player('youtube-player', {
        height: '0',
        width: '0',
        videoId: isPlaylistUrl ? undefined : videoId,
        playerVars: {
          autoplay: 1,
          loop: 1,
          playlist: isPlaylistUrl ? undefined : videoId,
          list: isPlaylistUrl ? videoId : undefined,
          listType: isPlaylistUrl ? 'playlist' : undefined,
        },
        events: {
          onReady: (event: YT.PlayerEvent) => {
            event.target.setVolume(volume);
            event.target.playVideo();
            setIsYoutubePlaying(true);
          },
          onError: () => {
            setYoutubeError('Could not play video. Check if it allows embedding.');
            setIsYoutubePlaying(false);
          },
          onStateChange: (event: YT.OnStateChangeEvent) => {
            if (event.data === window.YT.PlayerState.ENDED) {
              if (!isPlaylistUrl) {
                event.target.playVideo();
              }
            }
          },
        },
      });
    } else {
      setYoutubeError('YouTube player not loaded. Please try again.');
    }
  };

  // Stop YouTube
  const stopYouTube = () => {
    if (youtubePlayerRef.current) {
      youtubePlayerRef.current.stopVideo();
    }
    setIsYoutubePlaying(false);
    setYoutubeVideoId(null);
  };

  // Play Spotify - parses the pasted link and mounts the official embed
  // iframe. No OAuth, no Spotify Web API, no oEmbed network call: the embed
  // URL is built directly from the parsed type/id.
  const playSpotify = () => {
    const embed = extractSpotifyEmbed(spotifyUrl);
    if (!embed) {
      setSpotifyError(t('dashboard:calm_mode_spotify_error'));
      return;
    }
    setSpotifyError(null);
    setSpotifyEmbed(embed);
  };

  // Stop Spotify - unmount the embed (no JS control over a plain iframe)
  const stopSpotify = () => {
    setSpotifyEmbed(null);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      activeSoundsRef.current.forEach((_, soundId) => {
        stopSound(soundId);
      });
    };
  }, [stopSound]);

  // Spotify uses a plain iframe with no JS control, so we can only observe
  // whether the embed is mounted - not its precise play/pause state.
  const isSpotifyMounted = spotifyEmbed !== null;

  // Master volume only affects ambient sounds + YouTube (both are controllable
  // via JS); Spotify's volume lives inside its own embed and can't be driven
  // from here, so it's deliberately excluded from this gate.
  const hasVolumeControls = activeSounds.size > 0 || isYoutubePlaying;
  const isPlaying = activeSounds.size > 0 || isYoutubePlaying || isSpotifyMounted;
  const activeCount = activeSounds.size + (isYoutubePlaying ? 1 : 0) + (isSpotifyMounted ? 1 : 0);

  // Get display info for the button
  const getDisplayEmojis = () => {
    const activeEmojis = Array.from(activeSounds)
      .slice(0, 4)
      .map(id => sounds.find(s => s.id === id)?.emoji)
      .filter(Boolean);

    if (isYoutubePlaying) {
      activeEmojis.unshift('🎵');
    }

    if (isSpotifyMounted) {
      activeEmojis.unshift('💚');
    }

    return activeEmojis.slice(0, 4);
  };

  const displayEmojis = getDisplayEmojis();

  // Tooltip content with keyboard shortcut hint
  const tooltipContent = (
    <div className="text-center">
      <div>{isPlaying ? t('dashboard:calm_mode_active_count', { count: activeCount }) : t('dashboard:calm_mode_button')}</div>
      {timerEndTime && <div className="text-primary-300 text-xs">{t('dashboard:calm_mode_remaining', { timeRemaining })}</div>}
      {/* eslint-disable-next-line i18next/no-literal-string */}
      <div className="text-gray-400 text-xs mt-1">⌘⇧M to toggle</div>
    </div>
  );

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <Tooltip content={tooltipContent} side="bottom">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`relative inline-flex items-center justify-center max-sm:min-w-[44px] max-sm:min-h-[44px] p-2 rounded-full transition-colors focus:outline-none ${
            isPlaying
              ? 'text-primary-600 hover:bg-gray-100'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
          }`}
          aria-label={t('dashboard:calm_mode_button')}
        >
        <div className="flex items-center gap-1">
          {/* Timer badge - shown to the left of icons */}
          {timerEndTime && timeRemaining && (
            <div
              className="flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 rounded-full border border-amber-200"
              style={{
                animation: 'pulse-soft 2s ease-in-out infinite',
              }}
            >
              <span className="text-[10px]">⏱</span>
              <span className="text-[10px] font-semibold text-amber-700 tabular-nums min-w-[24px] text-right">
                {timeRemaining}
              </span>
            </div>
          )}
          <style>{`
            @keyframes pulse-soft {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.7; }
            }
          `}</style>
          <div className="flex items-center justify-center" style={{ width: 'clamp(18px, 2.5vh, 24px)', height: 'clamp(18px, 2.5vh, 24px)' }}>
            {isPlaying && displayEmojis.length > 0 ? (
              <div className="relative flex items-center h-6">
                {/* Stacked emojis - horizontal overlap with backgrounds */}
                <div className="flex items-center">
                  {displayEmojis.map((emoji, idx) => (
                    <span
                      key={idx}
                      className="w-5 h-5 flex items-center justify-center text-xs leading-none bg-gray-100 rounded-full border border-white shadow-sm"
                      style={{
                        marginLeft: idx === 0 ? 0 : '-8px',
                        zIndex: displayEmojis.length - idx,
                      }}
                    >
                      {emoji}
                    </span>
                  ))}
                </div>
                {/* Pulsing indicator */}
                <span className="absolute -top-0.5 -right-1 flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500"></span>
                </span>
              </div>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
              </svg>
            )}
          </div>
        </div>
        </button>
      </Tooltip>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="fixed sm:absolute right-2 sm:right-0 left-2 sm:left-auto mt-2 sm:w-96 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50">
          {/* Header - Dynamic gradient based on mood */}
          <div className={`bg-gradient-to-r ${getDynamicGradient()} px-4 py-3 transition-all duration-500`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
                </svg>
                <span className="text-white font-semibold text-sm">
                  {t('dashboard:calm_mode_title')}
                </span>
                {activeCount > 0 && (
                  <span className="px-1.5 py-0.5 bg-white/20 rounded text-xs text-white font-medium">
                    {t('dashboard:calm_mode_active_count', { count: activeCount })}
                  </span>
                )}
              </div>
              {isPlaying && (
                <button
                  onClick={stopAllSounds}
                  className="relative max-sm:after:absolute max-sm:after:inset-x-0 max-sm:after:top-1/2 max-sm:after:-translate-y-1/2 max-sm:after:h-[44px] px-2 py-1 bg-white/20 hover:bg-white/30 rounded text-xs text-white font-medium transition-colors"
                >
                  {t('dashboard:calm_mode_stop_all')}
                </button>
              )}
            </div>

            {/* Timer controls - always available */}
            {timerEndTime ? (
              <div className="flex items-center justify-between mt-2 max-sm:mt-4">
                <div className="flex items-center gap-2">
                  {/* eslint-disable-next-line i18next/no-literal-string */}
                  <span className="text-white/90 text-xs">⏱️ {timeRemaining}</span>
                </div>
                <button
                  onClick={cancelTimer}
                  className="max-sm:inline-flex max-sm:items-center max-sm:justify-center max-sm:min-h-[44px] max-sm:min-w-[44px] text-white/70 hover:text-white text-xs underline"
                >
                  {t('common:cancel')}
                </button>
              </div>
            ) : showCustomTimer ? (
              <div className="flex items-center gap-2 mt-2 max-sm:mt-[26px]">
                <NumberStepper
                  value={customMinutes}
                  onChange={setCustomMinutes}
                  min={1}
                  max={180}
                  step={1}
                  suffix="m"
                  size="sm"
                  variant="dark"
                />
                <button
                  onClick={() => {
                    startTimer(customMinutes);
                    setShowCustomTimer(false);
                    setCustomMinutes(25);
                  }}
                  className="relative max-sm:after:absolute max-sm:after:inset-x-0 max-sm:after:top-1/2 max-sm:after:-translate-y-1/2 max-sm:after:h-[44px] px-2 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-xs text-white font-medium transition-colors"
                >
                  {t('dashboard:calm_mode_start')}
                </button>
                {/* eslint-disable i18next/no-literal-string */}
                <button
                  onClick={() => {
                    setShowCustomTimer(false);
                    setCustomMinutes(25);
                  }}
                  className="max-sm:inline-flex max-sm:items-center max-sm:justify-center max-sm:min-h-[44px] max-sm:min-w-[44px] text-white/50 hover:text-white/80 text-sm"
                >
                  ✕
                </button>
                {/* eslint-enable i18next/no-literal-string */}
              </div>
            ) : (
              <div className="flex items-center gap-1.5 mt-2 max-sm:mt-[26px]">
                <span className="text-white/70 text-xs">{t('dashboard:calm_mode_timer_label')}</span>
                {timerOptions.map((opt) => (
                  <button
                    key={opt.minutes}
                    onClick={() => startTimer(opt.minutes)}
                    className="relative max-sm:after:absolute max-sm:after:inset-x-0 max-sm:after:top-1/2 max-sm:after:-translate-y-1/2 max-sm:after:h-[44px] px-1.5 py-0.5 bg-white/10 hover:bg-white/20 rounded text-xs text-white/90 transition-colors"
                  >
                    {opt.label}
                  </button>
                ))}
                <Tooltip content={t('dashboard:calm_mode_custom_timer_hint')}>
                  <button
                    onClick={() => setShowCustomTimer(true)}
                    className="relative max-sm:after:absolute max-sm:after:inset-x-0 max-sm:after:top-1/2 max-sm:after:-translate-y-1/2 max-sm:after:h-[44px] px-1.5 py-0.5 bg-white/10 hover:bg-white/20 rounded text-xs text-white/90 transition-colors"
                  >
                    +
                  </button>
                </Tooltip>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-100">
            <button
              onClick={() => setActiveTab('sounds')}
              className={`flex-1 px-4 py-2.5 max-sm:min-h-[44px] text-sm font-medium transition-colors ${
                activeTab === 'sounds'
                  ? 'text-primary-600 border-b-2 border-primary-500 bg-primary-50/50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <span className="flex items-center justify-center gap-1.5">
                <Icon name={ICONS.SOUND} size="sm" color="currentColor" />
                {t('dashboard:calm_mode_sounds')}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('youtube')}
              className={`flex-1 px-4 py-2.5 max-sm:min-h-[44px] text-sm font-medium transition-colors ${
                activeTab === 'youtube'
                  ? 'text-primary-600 border-b-2 border-primary-500 bg-primary-50/50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <span className="flex items-center justify-center gap-1.5">
                <FontAwesomeIcon icon={faYoutube} className="text-[#FF0000]" />
                {t('dashboard:calm_mode_youtube')}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('spotify')}
              className={`flex-1 px-4 py-2.5 max-sm:min-h-[44px] text-sm font-medium transition-colors ${
                activeTab === 'spotify'
                  ? 'text-primary-600 border-b-2 border-primary-500 bg-primary-50/50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <span className="flex items-center justify-center gap-1.5">
                <FontAwesomeIcon icon={faSpotify} className="text-[#1DB954]" />
                {t('dashboard:calm_mode_spotify')}
              </span>
            </button>
          </div>

          {/* Content */}
          <div className="p-3">
            {/* Completion celebration */}
            {showCompletion && lastCompletedMinutes && (
              <div className="mb-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg text-center">
                {/* eslint-disable-next-line i18next/no-literal-string */}
                <div className="text-2xl mb-1">🎉</div>
                <p className="text-sm font-semibold text-green-800">{t('dashboard:calm_mode_focus_complete')}</p>
                <p className="text-xs text-green-600 mb-2">
                  {t('dashboard:calm_mode_focus_minutes', {
                    minutes: lastCompletedMinutes,
                    minuteWord: lastCompletedMinutes === 1 ? 'minute' : 'minutes'
                  })}
                </p>
                <div className="flex justify-center gap-2">
                  <button
                    onClick={() => {
                      setShowCompletion(false);
                      startTimer(lastCompletedMinutes || 25);
                    }}
                    className="relative max-sm:after:absolute max-sm:after:inset-x-0 max-sm:after:top-1/2 max-sm:after:-translate-y-1/2 max-sm:after:h-[44px] px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-full transition-colors"
                  >
                    {t('dashboard:calm_mode_start_another')}
                  </button>
                  <button
                    onClick={() => setShowCompletion(false)}
                    className="relative max-sm:after:absolute max-sm:after:inset-x-0 max-sm:after:top-1/2 max-sm:after:-translate-y-1/2 max-sm:after:h-[44px] px-3 py-1 bg-white hover:bg-gray-50 text-gray-600 text-xs font-medium rounded-full border border-gray-200 transition-colors"
                  >
                    {t('dashboard:calm_mode_dismiss')}
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'sounds' && (
              <>
                {/* Quick Presets - Square tiles, collapsible */}
                <div className="mb-3">
                  <p className="text-[10px] font-medium text-gray-400 mb-1.5">{t('dashboard:calm_mode_presets')}</p>
                  <div className="grid grid-cols-6 gap-1.5">
                    {(presetsExpanded ? presets : presets.slice(0, 5)).map((preset) => {
                      const isPresetActive = preset.sounds.every(id => activeSounds.has(id)) &&
                                            activeSounds.size === preset.sounds.length;
                      return (
                        <Tooltip key={preset.id} content={`${preset.name}: ${preset.description}`} side="bottom">
                          <button
                            onClick={() => activatePreset(preset)}
                            className={`aspect-square flex items-center justify-center rounded-lg transition-all ${
                              isPresetActive
                                ? 'bg-primary-100 border-2 border-primary-400'
                                : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'
                            }`}
                          >
                            <span className="text-base">{preset.emoji}</span>
                          </button>
                        </Tooltip>
                      );
                    })}
                    {/* Expand/collapse button */}
                    <Tooltip content={presetsExpanded ? 'Show less' : 'Show more presets'} side="bottom">
                      <button
                        onClick={() => setPresetsExpanded(!presetsExpanded)}
                        className="aspect-square flex items-center justify-center rounded-lg bg-gray-50 border border-dashed border-gray-300 hover:bg-gray-100 transition-colors"
                      >
                        <span className="text-xs text-gray-400">
                          {presetsExpanded ? '−' : `+${presets.length - 5}`}
                        </span>
                      </button>
                    </Tooltip>
                  </div>
                </div>

                {/* Sound Grid - Collapsible (show 5 initially) */}
                <p className="text-[10px] font-medium text-gray-400 mb-1.5">{t('dashboard:calm_mode_sounds')}</p>
                <div className="grid grid-cols-6 gap-1.5">
                  {(soundsExpanded ? sounds : sounds.slice(0, 5)).map((sound) => {
                    const isActive = activeSounds.has(sound.id);
                    return (
                      <Tooltip key={sound.id} content={sound.name} side="bottom">
                        <button
                          onClick={() => toggleSound(sound.id)}
                          className={`aspect-square flex flex-col items-center justify-center rounded-lg transition-all ${
                            isActive
                              ? 'bg-primary-100 border-2 border-primary-400'
                              : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'
                          }`}
                        >
                          <span className="text-base">{sound.emoji}</span>
                          {isActive && (
                            <div className="flex gap-0.5 mt-0.5">
                              <span className="w-0.5 h-1.5 bg-primary-500 rounded-full animate-pulse" style={{ animationDelay: '0ms' }} />
                              <span className="w-0.5 h-2 bg-primary-500 rounded-full animate-pulse" style={{ animationDelay: '150ms' }} />
                              <span className="w-0.5 h-1 bg-primary-500 rounded-full animate-pulse" style={{ animationDelay: '300ms' }} />
                            </div>
                          )}
                        </button>
                      </Tooltip>
                    );
                  })}
                  {/* Expand/collapse button */}
                  <Tooltip content={soundsExpanded ? 'Show less' : 'Show more sounds'} side="bottom">
                    <button
                      onClick={() => setSoundsExpanded(!soundsExpanded)}
                      className="aspect-square flex items-center justify-center rounded-lg bg-gray-50 border border-dashed border-gray-300 hover:bg-gray-100 transition-colors"
                    >
                      <span className="text-xs text-gray-400">
                        {soundsExpanded ? '−' : `+${sounds.length - 5}`}
                      </span>
                    </button>
                  </Tooltip>
                </div>

                {/* Active sounds summary - more compact */}
                {activeSounds.size > 0 && (
                  <p className="mt-2 text-[10px] text-gray-500">
                    {/* eslint-disable-next-line i18next/no-literal-string */}
                    <span className="font-medium">🎧</span>{' '}
                    {Array.from(activeSounds)
                      .map(id => sounds.find(s => s.id === id)?.name)
                      .join(' + ')}
                  </p>
                )}

                {/* Tip - only when nothing active */}
                {activeSounds.size === 0 && !soundsExpanded && (
                  <p className="mt-2 text-[10px] text-gray-400">
                    {/* eslint-disable-next-line i18next/no-literal-string */}
                    <span className="font-medium">💡</span> {t('dashboard:calm_mode_keyboard_hint')}
                  </p>
                )}
              </>
            )}

            {activeTab === 'youtube' && (
              <div className="space-y-2">
                {/* YouTube URL Input - Compact */}
                <div className="flex gap-1.5">
                  <input
                    type="text"
                    value={youtubeUrl}
                    onChange={(e) => {
                      setYoutubeUrl(e.target.value);
                      setYoutubeError(null);
                    }}
                    placeholder="Video or playlist URL..."
                    className="flex-1 px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                  />
                  {isYoutubePlaying ? (
                    <Tooltip content={t('dashboard:calm_mode_stop')} side="bottom">
                      <button
                        onClick={stopYouTube}
                        className="relative max-sm:after:absolute max-sm:after:inset-x-0 max-sm:after:top-1/2 max-sm:after:-translate-y-1/2 max-sm:after:h-[44px] px-2.5 py-1.5 bg-red-500 text-white text-xs font-medium rounded-lg hover:bg-red-600 transition-colors"
                      >
                        {t('dashboard:calm_mode_stop')}
                      </button>
                    </Tooltip>
                  ) : (
                    <Tooltip content={t('dashboard:calm_mode_play')} side="bottom">
                      <button
                        onClick={playYouTube}
                        disabled={!youtubeUrl}
                        className="relative max-sm:after:absolute max-sm:after:inset-x-0 max-sm:after:top-1/2 max-sm:after:-translate-y-1/2 max-sm:after:h-[44px] px-2.5 py-1.5 bg-primary-500 text-primary-contrast text-xs font-medium rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {t('dashboard:calm_mode_play')}
                      </button>
                    </Tooltip>
                  )}
                </div>
                {youtubeError && (
                  <p className="text-[10px] text-red-600">{youtubeError}</p>
                )}

                {/* Currently playing - Compact */}
                {isYoutubePlaying && youtubeVideoId && (
                  <div className="flex items-center gap-2 p-2 bg-primary-50 rounded-lg">
                    {/* eslint-disable-next-line i18next/no-literal-string */}
                    <span className="text-sm">🎵</span>
                    <p className="flex-1 text-[10px] text-primary-700 truncate">{youtubeUrl}</p>
                    <div className="flex gap-0.5">
                      <span className="w-0.5 h-2 bg-primary-500 rounded-full animate-pulse" style={{ animationDelay: '0ms' }} />
                      <span className="w-0.5 h-3 bg-primary-500 rounded-full animate-pulse" style={{ animationDelay: '150ms' }} />
                      <span className="w-0.5 h-1.5 bg-primary-500 rounded-full animate-pulse" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                )}

                {/* Quick picks - Square tiles */}
                <div>
                  <p className="text-[10px] font-medium text-gray-400 mb-1.5">{t('dashboard:calm_mode_quick_picks')}</p>
                  <div className="grid grid-cols-6 gap-1.5">
                    {[
                      { name: 'lofi', emoji: '🎧', url: 'https://www.youtube.com/watch?v=jfKfPfyJRdk' },
                      { name: 'jazz', emoji: '🎷', url: 'https://www.youtube.com/watch?v=PnQId6so0Ac' },
                      { name: 'classical', emoji: '🎻', url: 'https://www.youtube.com/watch?v=mIYzp5rcTvU' },
                      { name: 'piano', emoji: '🎹', url: 'https://www.youtube.com/watch?v=HSOtku1j600' },
                      { name: 'ambient', emoji: '🌌', url: 'https://www.youtube.com/watch?v=S_MOd40zlYU' },
                      { name: 'nature', emoji: '🌿', url: 'https://www.youtube.com/watch?v=eKFTSSKCzWA' },
                    ].map((suggestion) => (
                      <Tooltip key={suggestion.name} content={t(`dashboard:calm_mode_quick_picks_${suggestion.name}`)} side="bottom">
                        <button
                          onClick={() => {
                            setYoutubeUrl(suggestion.url);
                            setYoutubeError(null);
                          }}
                          className={`aspect-square flex items-center justify-center rounded-lg transition-all ${
                            youtubeUrl === suggestion.url
                              ? 'bg-primary-100 border-2 border-primary-400'
                              : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'
                          }`}
                        >
                          <span className="text-base">{suggestion.emoji}</span>
                        </button>
                      </Tooltip>
                    ))}
                  </div>
                </div>

                {/* Mixing note - Compact */}
                {activeSounds.size > 0 && (
                  <p className="text-[10px] text-gray-400">
                    {/* eslint-disable-next-line i18next/no-literal-string */}
                    <span className="font-medium">🎧</span> {t('dashboard:calm_mode_mixing_note', {
                      count: activeSounds.size
                    })}
                  </p>
                )}

                {/* Hidden YouTube player */}
                <div id="youtube-player" ref={youtubeContainerRef} className="hidden" />
              </div>
            )}

            {activeTab === 'spotify' && (
              <div className="space-y-2">
                {/* Spotify URL Input - Compact */}
                <div className="flex gap-1.5">
                  <input
                    type="text"
                    value={spotifyUrl}
                    onChange={(e) => {
                      setSpotifyUrl(e.target.value);
                      setSpotifyError(null);
                    }}
                    placeholder={t('dashboard:calm_mode_spotify_placeholder')}
                    className="flex-1 px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                  />
                  {isSpotifyMounted ? (
                    <Tooltip content={t('dashboard:calm_mode_stop')} side="bottom">
                      <button
                        onClick={stopSpotify}
                        className="relative max-sm:after:absolute max-sm:after:inset-x-0 max-sm:after:top-1/2 max-sm:after:-translate-y-1/2 max-sm:after:h-[44px] px-2.5 py-1.5 bg-red-500 text-white text-xs font-medium rounded-lg hover:bg-red-600 transition-colors"
                      >
                        {t('dashboard:calm_mode_stop')}
                      </button>
                    </Tooltip>
                  ) : (
                    <Tooltip content={t('dashboard:calm_mode_play')} side="bottom">
                      <button
                        onClick={playSpotify}
                        disabled={!spotifyUrl}
                        className="relative max-sm:after:absolute max-sm:after:inset-x-0 max-sm:after:top-1/2 max-sm:after:-translate-y-1/2 max-sm:after:h-[44px] px-2.5 py-1.5 bg-primary-500 text-primary-contrast text-xs font-medium rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {t('dashboard:calm_mode_play')}
                      </button>
                    </Tooltip>
                  )}
                </div>
                {spotifyError && (
                  <p className="text-[10px] text-red-600">{spotifyError}</p>
                )}

                {/* Premium caveat - Spotify's embed only plays full tracks under specific conditions */}
                <p className="text-[10px] text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-2 py-1.5">
                  {/* eslint-disable-next-line i18next/no-literal-string */}
                  <span className="font-medium">💚</span> {t('dashboard:calm_mode_spotify_premium_note')}
                </p>

                {/* Spotify embed - official iframe, unmodified UI. Breaks out of the
                    content area's horizontal p-3 padding (-mx-3) so it gets the full
                    panel width - Spotify's single-track card has a ~300px min-width
                    that clips/scrolls if given anything narrower. */}
                {spotifyEmbed && (
                  <div className="-mx-3 rounded-lg overflow-hidden border border-gray-100">
                    <iframe
                      key={`${spotifyEmbed.type}-${spotifyEmbed.id}`}
                      title={t('dashboard:calm_mode_spotify')}
                      src={`https://open.spotify.com/embed/${spotifyEmbed.type}/${spotifyEmbed.id}`}
                      width="100%"
                      height={getSpotifyEmbedHeight(spotifyEmbed.type)}
                      style={{ border: 0 }}
                      allow="encrypted-media; clipboard-write; fullscreen; picture-in-picture"
                      loading="lazy"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Volume controls - only for sources master volume can actually drive (sounds + YouTube) */}
            {hasVolumeControls && (
              <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
                {/* Individual sound volumes */}
                {activeSounds.size > 0 && (
                  <div className="space-y-2">
                    {Array.from(activeSounds).map(soundId => {
                      const sound = sounds.find(s => s.id === soundId);
                      if (!sound) return null;
                      const soundVol = getSoundVolume(soundId);
                      return (
                        <div key={soundId} className="flex items-center gap-2">
                          <span className="text-sm w-5">{sound.emoji}</span>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={soundVol}
                            onChange={(e) => updateSoundVolume(soundId, Number(e.target.value))}
                            className="flex-1 h-1 bg-gray-200 rounded-full appearance-none cursor-pointer accent-primary-500"
                          />
                          <span className="text-[10px] font-medium text-gray-400 w-7">{soundVol}%</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* YouTube volume */}
                {isYoutubePlaying && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm w-5">🎵</span>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={volume}
                      onChange={(e) => {
                        const newVol = Number(e.target.value);
                        setVolume(newVol);
                        if (youtubePlayerRef.current) {
                          youtubePlayerRef.current.setVolume(newVol);
                        }
                      }}
                      className="flex-1 h-1 bg-gray-200 rounded-full appearance-none cursor-pointer accent-primary-500"
                    />
                    <span className="text-[10px] font-medium text-gray-400 w-7">{volume}%</span>
                  </div>
                )}

                {/* Master volume */}
                <div className="flex items-center gap-2 pt-2 border-t border-gray-50">
                  <Icon name={ICONS.SOUND} size="xs" color="text-gray-400" />
                  <span className="text-[10px] font-medium text-gray-500">{t('dashboard:calm_mode_master_volume')}</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={volume}
                    onChange={(e) => setVolume(Number(e.target.value))}
                    className="flex-1 h-1.5 bg-gray-200 rounded-full appearance-none cursor-pointer accent-primary-500"
                  />
                  <span className="text-[10px] font-medium text-gray-500 w-7">{volume}%</span>
                </div>
              </div>
            )}
          </div>

          {/* Footer with keyboard shortcut */}
          <div className="px-3 py-2 bg-gray-50 border-t border-gray-100">
            <p className="text-[10px] text-gray-400 text-center">
              {t('dashboard:calm_mode_keyboard_hint')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// YouTube IFrame API types
declare global {
  interface Window {
    YT: {
      Player: new (
        elementId: string,
        config: {
          height: string;
          width: string;
          videoId?: string;
          playerVars?: {
            autoplay?: number;
            loop?: number;
            playlist?: string;
            list?: string;
            listType?: string;
          };
          events?: {
            onReady?: (event: YT.PlayerEvent) => void;
            onError?: (event: YT.OnErrorEvent) => void;
            onStateChange?: (event: YT.OnStateChangeEvent) => void;
          };
        }
      ) => YT.Player;
      PlayerState: {
        ENDED: number;
        PLAYING: number;
        PAUSED: number;
        BUFFERING: number;
        CUED: number;
      };
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

declare namespace YT {
  interface Player {
    playVideo(): void;
    pauseVideo(): void;
    stopVideo(): void;
    setVolume(volume: number): void;
    getVolume(): number;
    destroy(): void;
  }
  interface PlayerEvent {
    target: Player;
  }
  interface OnErrorEvent {
    data: number;
  }
  interface OnStateChangeEvent {
    data: number;
    target: Player;
  }
}
